// ============= Types =============
export interface TokenInfo {
  token: string;
  expiresOn: Date;
  scopes: string[];
}

interface MSALTokenData {
  secret: string;
  homeAccountId: string;
  environment: string;
  credentialType: string;
  clientId: string;
  realm: string;
  target: string;
  cachedAt: number;
  expiresOn: number;
  extendedExpiresOn: number;
}

// ============= Constants =============
const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes buffer
const ACCESS_TOKEN_KEY_PATTERN = /.*-accesstoken-.*/;

// ============= Token Management =============
export const tokenManager = {
  getMsalTokenFromStorage(): MSALTokenData | null {
    try {
      // Find the access token entry in sessionStorage
      const tokenKey = Object.keys(sessionStorage).find((key) =>
        ACCESS_TOKEN_KEY_PATTERN.test(key)
      );

      if (!tokenKey) return null;

      const tokenData = JSON.parse(sessionStorage.getItem(tokenKey) || '');
      return tokenData;
    } catch (error) {
      console.error('Error getting MSAL token from storage:', error);
      return null;
    }
  },

  isTokenExpired(expiresOn: number | Date): boolean {
    const now = Date.now() / 1000; // Convert to seconds
    const expiryTime =
      expiresOn instanceof Date ? expiresOn.getTime() / 1000 : expiresOn;

    return now + REFRESH_BUFFER_MS / 1000 >= expiryTime;
  },

  validateToken(tokenInfo: TokenInfo | null): boolean {
    if (!tokenInfo || !tokenInfo.token) return false;
    return !this.isTokenExpired(tokenInfo.expiresOn);
  },

  validateMsalToken(): boolean {
    const tokenData = this.getMsalTokenFromStorage();
    if (!tokenData) return false;
    return !this.isTokenExpired(tokenData.expiresOn);
  },

  clearExpiredToken(): void {
    const tokenKey = Object.keys(sessionStorage).find((key) =>
      ACCESS_TOKEN_KEY_PATTERN.test(key)
    );

    if (tokenKey) {
      sessionStorage.removeItem(tokenKey);
      console.log('Cleared expired MSAL token');
    }
  },

  checkAndHandleTokenExpiration(): boolean {
    const tokenData = this.getMsalTokenFromStorage();

    if (!tokenData) return false;

    if (this.isTokenExpired(tokenData.expiresOn)) {
      this.clearExpiredToken();
      return false;
    }

    return true;
  },

  logTokenInfo(token: string, purpose: string): void {
    console.log(
      `✅ Token acquired for ${purpose}:`,
      token.substring(0, 10) + '...'
    );
  },

  hasRequiredScopes(tokenInfo: TokenInfo, requiredScopes: string[]): boolean {
    return requiredScopes.every((scope) => tokenInfo.scopes.includes(scope));
  },
};
